import { useUrlListState } from '@/hooks/useUrlListState';
import type { InvoiceSortBy, InvoiceSortDir } from '../../../store/useInvoiceStore';
import { BILLING_INTERVALS, type BillingInterval } from '@/types/billing';

export type InvoiceStatusFilter =
  | 'all'
  | 'Draft'
  | 'Issued'
  | 'Sent'
  | 'Paid'
  | 'Cancelled';

export type InvoiceIntervalFilter = 'all' | BillingInterval;

export const CURRENT_YEAR = new Date().getFullYear();

// URL list state keeps every filter as a string; 'all' is the "no filter" sentinel.
const STATUS_FILTERS: readonly InvoiceStatusFilter[] = ['all', 'Draft', 'Issued', 'Sent', 'Paid', 'Cancelled'];
const INTERVAL_FILTERS: readonly InvoiceIntervalFilter[] = ['all', ...BILLING_INTERVALS];
const YEAR_FILTERS: readonly string[] = [
  'all',
  ...[CURRENT_YEAR + 1, CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map(String),
];
const MONTH_FILTERS: readonly string[] = ['all', ...Array.from({ length: 12 }, (_, i) => String(i + 1))];
const SORT_BY_VALUES: readonly InvoiceSortBy[] = [
  'Object', 'Customer', 'InvoiceNumber', 'Period', 'Status', 'Gross', 'DueDate',
];
const SORT_DIR_VALUES: readonly InvoiceSortDir[] = ['Asc', 'Desc'];
type InvoiceGroupFilter = 'none' | 'object';
const GROUP_FILTERS: readonly InvoiceGroupFilter[] = ['none', 'object'];

const toNumberFilter = (value: string): number | 'all' => (value === 'all' ? 'all' : Number(value));

/** Filters, sort, grouping and page of the invoice list, kept in the URL (FAFD-50). */
export function useInvoiceListState() {
  // FAFD-14: starts as a flat list, period newest first (server-side, before paging).
  const [listState, setListState] = useUrlListState(
    {
      q: '',
      customer: 'all',
      status: 'all' as InvoiceStatusFilter,
      year: String(CURRENT_YEAR),
      month: 'all',
      interval: 'all' as InvoiceIntervalFilter,
      sort: 'Period' as InvoiceSortBy,
      dir: 'Desc' as InvoiceSortDir,
      // Grouped, the server orders by object first and by sortBy inside each group. Kept in the
      // URL with the page, since a page number only means something together with its order.
      group: 'none' as InvoiceGroupFilter,
      page: 1,
    },
    {
      status: STATUS_FILTERS,
      interval: INTERVAL_FILTERS,
      year: YEAR_FILTERS,
      month: MONTH_FILTERS,
      sort: SORT_BY_VALUES,
      dir: SORT_DIR_VALUES,
      group: GROUP_FILTERS,
    },
  );
  return {
    searchTerm: listState.q,
    filterCustomerId: listState.customer,
    filterStatus: listState.status,
    filterInterval: listState.interval,
    sortBy: listState.sort,
    sortDir: listState.dir,
    listPage: listState.page,
    filterYear: toNumberFilter(listState.year),
    filterMonth: toNumberFilter(listState.month),
    groupByObject: listState.group === 'object',
    setListState,
  };
}
